import * as React from 'react';
import { EnvironmentContext, TranslationsContext } from '@telia-common-components/shared';
import { AnalyticsProvider, AnalyticsTrigger, Button, Grid, GridColumn, Notice, Separator } from 'telia-front-react';
import { useInternetCalculator } from '../InternetCalculatorProvider';
import { useInternetOffers } from '../InternetOffersProvider';
import { getIsDealerFromAuth } from '../util/InternetCalculatorUtil';
import { hasFWAOffers } from '../util/InternetOfferUtil';
export const AddressProductsNotices = () => {
    const { addressProductsState } = useInternetOffers();
    const { tier } = React.useContext(EnvironmentContext);
    const customerHasProduct = !!addressProductsState.addressProducts.customerProduct;
    return (React.createElement(AnalyticsProvider, { group: "AddressProductsNotice" },
        customerHasProduct ? (React.createElement(CustomerHasInternetNotice, { url: resolveEditProductUrl(addressProductsState.addressProducts.customerProduct, addressProductsState.addressProducts.customerProductOrigin, tier) })) : (React.createElement(AddressHasInternetNotice, null)),
        React.createElement(DealerNotice, null)));
};
const DealerNotice = () => {
    const { addressProductsState, calculatorState } = useInternetOffers();
    const { auth, isSecureNet } = useInternetCalculator();
    const isDealer = getIsDealerFromAuth(auth);
    if (!isDealer) {
        return null;
    }
    if (hasFWAOffers(calculatorState.internetOffers) && !isSecureNet) {
        return React.createElement(DealerAddressHasInternetAndFWAOffersNotice, null);
    }
    const customerHasProduct = !!addressProductsState.addressProducts.customerProduct;
    return customerHasProduct ? React.createElement(DealerCustomerHasInternetNotice, null) : React.createElement(DealerAddressHasInternetNotice, null);
};
const DealerAddressHasInternetAndFWAOffersNotice = () => {
    const { translate } = React.useContext(TranslationsContext);
    return (React.createElement(React.Fragment, null,
        React.createElement(Separator, { spacing: "xsmall", color: "invisible" }),
        React.createElement(Notice, { align: "center", type: "employee" }, translate('internetCalculator.addressHasInternetNotice'))));
};
const DealerAddressHasInternetNotice = () => {
    const { addressProductsState } = useInternetOffers();
    const { translate } = React.useContext(TranslationsContext);
    const { tier } = React.useContext(EnvironmentContext);
    const argosUrl = resolveArgosUrl(addressProductsState.addressProducts.productId, tier);
    const clientSearchUrl = resolveClientSearchUrl(tier);
    const viewUrl = addressProductsState.addressProducts.productOrigin === 'ARGOS' ? argosUrl : clientSearchUrl;
    const buttonLabel = `internetCalculator.dealerAddressHasInternetButton.${addressProductsState.addressProducts.productOrigin}`;
    return (React.createElement(React.Fragment, null,
        React.createElement(Separator, { spacing: "xsmall", color: "invisible" }),
        React.createElement(Notice, { align: "center", type: "employee" },
            React.createElement(Grid, { align: ['between-xs', 'middle-xs'], noWrap: ['sm'] },
                React.createElement(GridColumn, { width: ['xs', 'max-sm'] },
                    translate('internetCalculator.dealerAddressHasInternetNotice'),
                    React.createElement(CommunicationNumbers, { communicationNumbers: addressProductsState.addressProducts.communicationNumbers })),
                React.createElement(GridColumn, { width: ['xs', 'min-sm'] },
                    React.createElement(AnalyticsTrigger, { trigger: { id: 'Button - Dealer: address has internet', callback: 'onClick' } },
                        React.createElement(Button, { text: translate(buttonLabel), type: "employee", url: viewUrl, iconRight: "#arrow-right" })))))));
};
const DealerCustomerHasInternetNotice = () => {
    const { isSecureNet } = useInternetCalculator();
    const { translate } = React.useContext(TranslationsContext);
    const labelPrefix = isSecureNet ? 'secureNetCalculator' : 'internetCalculator';
    return (React.createElement(React.Fragment, null,
        React.createElement(Separator, { spacing: "xsmall", color: "invisible" }),
        React.createElement(Notice, { align: "center", type: "employee" }, translate(`${labelPrefix}.dealerHasInternetNotice`))));
};
const CustomerHasInternetNotice = (props) => {
    const { url } = props;
    const { translate } = React.useContext(TranslationsContext);
    const { isSecureNet } = useInternetCalculator();
    const labelPrefix = isSecureNet ? 'secureNetCalculator' : 'internetCalculator';
    return (React.createElement(Notice, { align: "center", type: "attention" },
        React.createElement(Grid, { align: ['between-xs', 'middle-xs'], noWrap: ['sm'] },
            React.createElement(GridColumn, { width: ['xs', 'max-sm'] }, translate(`${labelPrefix}.customerHasInternetNotice`)),
            React.createElement(GridColumn, { width: ['xs', 'min-sm'] },
                React.createElement(AnalyticsTrigger, { trigger: { id: 'Button - customer has product on address', callback: 'onClick' } },
                    React.createElement(Button, { text: translate(`${labelPrefix}.customerHasInternetButton`), url: url, iconRight: "#arrow-right" }))))));
};
const AddressHasInternetNotice = () => {
    const { translate } = React.useContext(TranslationsContext);
    const { isSecureNet } = useInternetCalculator();
    return (React.createElement(Notice, { align: "center", type: "error" }, translate(`${isSecureNet ? 'secureNetCalculator' : 'internetCalculator'}.customerAddressHasInternetNotice`)));
};
const CommunicationNumbers = (props) => {
    var _a;
    return React.createElement(React.Fragment, null, (_a = props.communicationNumbers) === null || _a === void 0 ? void 0 : _a.map((number) => React.createElement(CommunicationNumber, { key: number, communicationNumber: number })));
};
const CommunicationNumber = (props) => {
    const { translate } = React.useContext(TranslationsContext);
    const [showSuccess, setShowSuccess] = React.useState(false);
    const handleClick = () => {
        var _a;
        setShowSuccess(true);
        (_a = navigator === null || navigator === void 0 ? void 0 : navigator.clipboard) === null || _a === void 0 ? void 0 : _a.writeText(props.communicationNumber);
        setTimeout(() => setShowSuccess(false), 3000);
    };
    return (React.createElement(React.Fragment, null,
        ` ${props.communicationNumber} `,
        React.createElement(Button, { type: "text-color", link: true, icon: "#copy", iconSize: "xs", text: translate('internetCalculator.copyCommunicationNumber'), onClick: () => handleClick() }),
        showSuccess && React.createElement("span", { className: "text-success" }, translate('internetCalculator.copyCommunicationNumberSuccess')),
        React.createElement("br", null)));
};
const resolveArgosUrl = (productId, tier) => {
    return `https://argos${resolveEnv(tier)}.telia.ee/pls/apex_argos/sysadm.l_leping_pkt.show_form?p_rowid=${productId}`;
};
const resolveClientSearchUrl = (tier) => {
    return `https://iseteenindus${resolveEnv(tier)}.telia.ee/teenindus/otsing`;
};
const resolveEnv = (tier) => {
    if (['dev', 'test'].includes(tier)) {
        return `-${tier}`;
    }
    return '';
};
const resolveEditProductUrl = (productId, origin, tier) => {
    return `https://iseteenindus${resolveEnv(tier)}.telia.ee/teenused/${productId}_${origin}`;
};
